import { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Chip,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  THRESHOLD_FIELDS,
  THRESHOLD_GROUPS,
  loadOverrides,
  saveOverrides,
  shippedForGroup,
  thresholdsForGroup,
  validateThresholds,
} from "./findings";

/** Same order as THRESHOLD_FIELDS, grouped under the rule each number feeds. */
const BY_RULE = THRESHOLD_FIELDS.reduce((acc, field) => {
  const bucket = acc.find((b) => b.rule === field.rule);
  if (bucket) bucket.fields.push(field);
  else acc.push({ rule: field.rule, fields: [field] });
  return acc;
}, []);

const withoutKey = (overrides, group, key) => {
  const patch = { ...(overrides[group] || {}) };
  delete patch[key];
  return { ...overrides, [group]: patch };
};

/**
 * Editor for the numbers the findings rules compare against.
 *
 * Edits are sparse — only the keys deliberately changed are stored, so a
 * retuned shipped default still reaches anyone who never touched that field.
 * An incoherent warn/critical pair is refused rather than saved, because a
 * critical sitting on the safe side of warn silently downgrades every hit.
 *
 * ponytail: stored in localStorage, so this is per-browser and carries no audit
 * trail — same trade as the inspection record. Move to a per-org backend record
 * the day two engineers must analyse one log against the same numbers; the
 * shape saved here is already what that endpoint would store.
 */
const LogThresholds = () => {
  const theme = useTheme();
  const [group, setGroup] = useState("base");
  const [overrides, setOverrides] = useState(loadOverrides);
  // Text being typed, so a half-entered "3." survives the keystroke.
  const [draft, setDraft] = useState({});

  const effective = useMemo(() => thresholdsForGroup(group, overrides), [group, overrides]);
  const shipped = useMemo(() => shippedForGroup(group), [group]);

  // Validate what is on screen, not what was saved: a refused pair is never
  // stored, so validating saved state would leave the offending field silent.
  const onScreen = useMemo(() => {
    const next = { ...effective };
    for (const field of THRESHOLD_FIELDS) {
      const text = draft[`${group}:${field.key}`];
      if (text == null) continue;
      const value = Number(text);
      if (text.trim() !== "" && Number.isFinite(value) && value > 0) next[field.key] = value;
    }
    return next;
  }, [effective, draft, group]);
  const pairErrors = useMemo(() => validateThresholds(onScreen), [onScreen]);

  const groupOverrides = overrides[group] || {};
  const tunedCount = THRESHOLD_GROUPS.reduce(
    (n, g) => n + Object.keys(overrides[g.key] || {}).length,
    0
  );

  const apply = (next) => {
    setOverrides(saveOverrides(next));
  };

  const commit = (key, text) => {
    setDraft((d) => ({ ...d, [`${group}:${key}`]: text }));
    const value = Number(text);
    if (text.trim() === "" || !Number.isFinite(value) || value <= 0) return;
    const next = { ...overrides, [group]: { ...groupOverrides, [key]: value } };
    // Refuse a set where critical sits on the safe side of warn — the field
    // keeps showing it, blurring puts the last saved number back.
    if (Object.keys(validateThresholds(thresholdsForGroup(group, next))).length) return;
    apply(next);
  };

  const clearDraft = (key) =>
    setDraft((d) => {
      const next = { ...d };
      delete next[`${group}:${key}`];
      return next;
    });

  const reset = (key) => {
    clearDraft(key);
    apply(withoutKey(overrides, group, key));
  };

  const resetAll = () => {
    setDraft({});
    apply({});
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
        The numbers the findings rules compare against. Changing one changes which findings fire on
        every log you open, and every RCA draft you export says which numbers were used. Saved in
        this browser only.
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2, flexWrap: "wrap" }}>
        <ToggleButtonGroup size="small" exclusive value={group} onChange={(e, v) => v && setGroup(v)}>
          {THRESHOLD_GROUPS.map((g) => (
            <ToggleButton key={g.key} value={g.key} sx={{ textTransform: "none", px: 1.5 }}>
              {g.label}
              {Object.keys(overrides[g.key] || {}).length > 0 && " •"}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {tunedCount > 0 && (
          <Button
            size="small"
            startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
            onClick={resetAll}
            sx={{ textTransform: "none", color: theme.palette.text.secondary }}
          >
            Reset all ({tunedCount})
          </Button>
        )}
      </Box>

      <Typography variant="caption" sx={{ display: "block", mt: 1, color: theme.palette.text.secondary }}>
        {group === "base"
          ? "Applies to every airframe that has no profile of its own."
          : `Applies to logs whose vehicle type contains “${group}”. Falls back to the all-airframes set for anything not set here.`}
      </Typography>

      <Divider sx={{ my: 2.5 }} />

      {BY_RULE.map(({ rule, fields }) => (
        <Box key={rule} sx={{ mb: 3 }}>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary, textTransform: "uppercase", letterSpacing: 0.4 }}
          >
            {rule}
          </Typography>
          <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
            {fields.map((field) => {
              const isOverridden = field.key in groupOverrides;
              const inherited = thresholdsForGroup(group, withoutKey(overrides, group, field.key))[field.key];
              const draftKey = `${group}:${field.key}`;
              const shown = draft[draftKey] ?? String(effective[field.key]);
              const parsed = Number(shown);
              const badNumber = shown.trim() === "" || !Number.isFinite(parsed) || parsed <= 0;
              const error = badNumber ? "Must be a positive number." : pairErrors[field.key] || "";
              return (
                <Box
                  key={field.key}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${alpha(
                      error ? theme.palette.error.main : theme.palette.divider,
                      error ? 0.35 : 0.6
                    )}`,
                    backgroundColor: error ? alpha(theme.palette.error.main, 0.06) : "transparent",
                    display: "flex",
                    gap: 1.5,
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 240 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                        {field.label}
                      </Typography>
                      {isOverridden && (
                        <Chip
                          label="tuned"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: 11,
                            color: theme.palette.warning.main,
                            backgroundColor: alpha(theme.palette.warning.main, 0.14),
                          }}
                        />
                      )}
                    </Box>
                    {field.why && (
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        {field.why}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={shown}
                      onChange={(e) => commit(field.key, e.target.value)}
                      onBlur={() => clearDraft(field.key)}
                      error={!!error}
                      helperText={
                        error ||
                        (isOverridden
                          ? `Reset → ${inherited}`
                          : shipped[field.key] === effective[field.key]
                            ? "Shipped default"
                            : "Inherited from all airframes")
                      }
                      inputProps={{ step: field.step, min: 0, "aria-label": field.label }}
                      sx={{ width: 132 }}
                    />
                    {field.unit && (
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 1.25 }}>
                        {field.unit}
                      </Typography>
                    )}
                    <Button
                      size="small"
                      onClick={() => reset(field.key)}
                      disabled={!isOverridden}
                      sx={{ minWidth: 0, px: 1, mt: 0.25, color: theme.palette.text.secondary }}
                      aria-label={`Reset ${field.label}`}
                    >
                      <RestartAltIcon sx={{ fontSize: 18 }} />
                    </Button>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default LogThresholds;
